import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import React from 'react'

import { DoneState } from '../lib/useDoneState'
import { NotesState } from '../lib/useNotes'
import { Voiceover } from '../types'

import { NoteButton } from './NoteButton'

type Props = {
  rows: Voiceover[]
  done: DoneState
  notes: NotesState
  // index is the position within the full (filtered) list, so detail nav still works.
  onRowClick: (index: number) => void
}

type PoiGroup = {
  poiIndex: number
  poiTitle: string
  items: { row: Voiceover, index: number }[]
}

function groupByPoi (rows: Voiceover[]): PoiGroup[] {
  const groups: PoiGroup[] = []
  const map = new Map<number, PoiGroup>()
  rows.forEach((row, index) => {
    let group = map.get(row.poiIndex)
    if (!group) {
      group = { poiIndex: row.poiIndex, poiTitle: row.poiTitle, items: [] }
      map.set(row.poiIndex, group)
      groups.push(group)
    }
    group.items.push({ row, index })
  })
  return groups.sort((a, b) => a.poiIndex - b.poiIndex)
}

const preview = (text: string): string =>
  text.replace(/\s+/g, ' ').trim().slice(0, 90)

// One collapsible table per POI. The POI is the section header (no POI column),
// and its checkbox marks the whole POI done/undone at once.
export const VoiceoverTable: React.FC<Props> = ({
  rows,
  done,
  notes,
  onRowClick
}) => {
  const groups = groupByPoi(rows)

  return (
    <Box sx={{ height: '100%', overflow: 'auto', bgcolor: 'background.paper' }}>
      {groups.map((group) => {
        const ids = group.items.map((i) => i.row.suggestedFileName)
        const doneCount = ids.filter((id) => done.isDone(id)).length
        const allDone = doneCount === ids.length
        const someDone = doneCount > 0 && !allDone

        return (
          <Accordion
            key={group.poiIndex}
            disableGutters
            square
            elevation={0}
            sx={{
              '&::before': { display: 'none' },
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{ display: 'flex', alignItems: 'center' }}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Ganzen POI abhaken">
                  <Checkbox
                    size="small"
                    checked={allDone}
                    indeterminate={someDone}
                    onChange={() => done.setMany(ids, !allDone)}
                    sx={{
                      '&.MuiCheckbox-indeterminate': {
                        color: 'warning.main'
                      }
                    }}
                  />
                </Tooltip>
              </Box>
              <Typography sx={{ fontWeight: 600, alignSelf: 'center' }}>
                {group.poiIndex}. {group.poiTitle}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1, alignSelf: 'center' }}
              >
                ({doneCount}/{ids.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Erledigt</TableCell>
                    <TableCell align="center">Notiz</TableCell>
                    <TableCell>Abschnitt</TableCell>
                    <TableCell>Nugget</TableCell>
                    <TableCell sx={{ minWidth: 280 }}>Text</TableCell>
                    <TableCell>Dateiname</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.items.map(({ row, index }) => {
                    const isDone = done.isDone(row.suggestedFileName)
                    return (
                      <TableRow
                        key={row.suggestedFileName}
                        hover
                        sx={{ cursor: 'pointer', opacity: isDone ? 0.5 : 1 }}
                        onClick={() => onRowClick(index)}
                      >
                        <TableCell
                          align="center"
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={isDone}
                            onChange={() => done.toggle(row.suggestedFileName)}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <NoteButton
                            note={notes.get(row.suggestedFileName)}
                            onChange={(t) => notes.set(row.suggestedFileName, t)}
                          />
                        </TableCell>
                        <TableCell>{row.sectionTitle}</TableCell>
                        <TableCell>{row.nuggetTitle}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {preview(row.text)}
                            {row.text.length > 90 ? '…' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                            >
                              {row.suggestedFileName}
                            </Typography>
                            <Tooltip title="Dateiname kopieren">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigator.clipboard?.writeText(
                                    row.suggestedFileName
                                  )
                                }}
                              >
                                <ContentCopyIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Box>
  )
}
